import { useState } from 'react';
import { Button } from '@/components/ui';
import type { TaskDefinition } from '@/types/project-command';
import type { useProjectCommand } from '@/hooks/useProjectCommand';

export function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label>
      {label}
      <input
        type="number"
        required
        min="0"
        max="4294967295"
        step="1"
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? NaN : Number(e.target.value))
        }
      />
    </label>
  );
}

export function TaskEditor({
  projectId,
  storageId,
  taskId,
  task,
  command,
  disabled,
  operational = false,
  progressAllowed = true,
}: {
  projectId: string;
  storageId: string;
  taskId?: number;
  task?: TaskDefinition;
  command: ReturnType<typeof useProjectCommand>;
  disabled: boolean;
  operational?: boolean;
  progressAllowed?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState(JSON.stringify(task));
  const [assignees, setAssignees] = useState(task?.assignees.join(', ') ?? '');
  const changed = source !== JSON.stringify(task);
  const [draft, setDraft] = useState<TaskDefinition>(
    () =>
      task ?? {
        title: '',
        description: '',
        taskType: 'Task',
        priority: 'Medium',
        status: 'ToDo',
        assignees: [],
        estimatedMinutes: 60,
        loggedMinutes: 0,
        dueAt: null,
      }
  );
  const [dueText, setDueText] = useState(() =>
    task?.dueAt != null && task.dueAt <= 8640000000000
      ? new Date(task.dueAt * 1000).toISOString().slice(0, 16)
      : ''
  );
  const locked = disabled || command.busy || command.pending || command.blocked;
  return (
    <div className="backendForm space-y-4">
      <Button
        variant="outline"
        size="sm"
        disabled={locked}
        onClick={() => {
          setDraft(
            task ?? {
              title: '',
              description: '',
              taskType: 'Task',
              priority: 'Medium',
              status: 'ToDo',
              assignees: [],
              estimatedMinutes: 60,
              loggedMinutes: 0,
              dueAt: null,
            }
          );
          setDueText(
            task?.dueAt != null && task.dueAt <= 8640000000000
              ? new Date(task.dueAt * 1000).toISOString().slice(0, 16)
              : ''
          );
          setSource(JSON.stringify(task));
          setAssignees(task?.assignees.join(', ') ?? '');
          setOpen(!open);
        }}
      >
        {taskId == null ? 'Add task' : 'Edit task #' + taskId}
      </Button>
      {open && (
        <form
          aria-label={
            taskId == null ? 'New milestone task' : 'Edit milestone task'
          }
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (changed) return;
            void command
              .run(
                taskId == null
                  ? {
                      kind: 'task-create',
                      projectId,
                      storageId,
                      payload: operational
                        ? {
                            ...draft,
                            ...(!progressAllowed && {
                              status: 'ToDo' as const,
                              loggedMinutes: 0,
                            }),
                            assignees: assignees
                              .split(',')
                              .map((id) => id.trim())
                              .filter(Boolean),
                          }
                        : draft,
                    }
                  : {
                      kind: 'task-update',
                      projectId,
                      storageId,
                      taskId,
                      payload: operational
                        ? {
                            ...draft,
                            ...(!progressAllowed && {
                              status: task?.status ?? 'ToDo',
                              loggedMinutes: task?.loggedMinutes ?? 0,
                            }),
                            assignees: assignees
                              .split(',')
                              .map((id) => id.trim())
                              .filter(Boolean),
                          }
                        : draft,
                    }
              )
              .then((result) => {
                if (result) setOpen(false);
              });
          }}
        >
          {changed && (
            <p role="alert">
              This task changed on the server. Close and reopen the editor
              before saving.
            </p>
          )}
          <fieldset disabled={locked || changed} className="space-y-4">
            <label>
              Task title
              <input
                required
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>
            <label>
              Task description
              <textarea
                aria-label="Task description"
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
              />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label>
                Task type
                <select
                  className="input bg-background text-foreground [color-scheme:dark]"
                  value={draft.taskType}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      taskType: e.target.value as TaskDefinition['taskType'],
                    })
                  }
                >
                  {['Feature', 'Bug', 'Task', 'Epic', 'Story'].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label>
                Priority
                <select
                  className="input bg-background text-foreground [color-scheme:dark]"
                  value={draft.priority}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      priority: e.target.value as TaskDefinition['priority'],
                    })
                  }
                >
                  {[
                    'Lowest',
                    'Low',
                    'Medium',
                    'High',
                    'Highest',
                    'Blocker',
                  ].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
            </div>
            <NumberField
              label="Estimated minutes"
              value={draft.estimatedMinutes}
              onChange={(value) =>
                setDraft({ ...draft, estimatedMinutes: value })
              }
            />
            <label>
              Due date and time (UTC, optional)
              <input
                type="datetime-local"
                min="1970-01-01T00:00"
                max="9999-12-31T23:59"
                className="[color-scheme:dark]"
                value={dueText}
                onChange={(e) => {
                  setDueText(e.target.value);
                  setDraft({
                    ...draft,
                    dueAt: e.target.value
                      ? new Date(e.target.value + 'Z').getTime() / 1000
                      : null,
                  });
                }}
              />
            </label>
            {operational && (
              <>
                <label>
                  Assignee account IDs (comma separated)
                  <textarea
                    aria-label="Assignee account IDs (comma separated)"
                    value={assignees}
                    onChange={(e) => setAssignees(e.target.value)}
                  />
                </label>
                <p className="text-sm text-muted-foreground">
                  Use registered worker accounts from the project team. Task
                  assignment tracks responsibility; it does not change
                  contractual staffing or payments.
                </p>
              </>
            )}
            {operational && progressAllowed && (
              <>
                <label>
                  Status
                  <select
                    aria-label="Status"
                    className="input bg-background text-foreground [color-scheme:dark]"
                    value={draft.status}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        status: e.target.value as TaskDefinition['status'],
                      })
                    }
                  >
                    {[
                      'ToDo',
                      'Open',
                      'InProgress',
                      'InReview',
                      'Done',
                      'Closed',
                    ].map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </label>
                <NumberField
                  label="Logged minutes"
                  value={draft.loggedMinutes}
                  onChange={(value) =>
                    setDraft({ ...draft, loggedMinutes: value })
                  }
                />
              </>
            )}
            <Button type="submit">Save task</Button>
          </fieldset>
        </form>
      )}
    </div>
  );
}
